import type { AnswersByQuestionId, QuestionRoutingRules } from '@/modules/routing'
import { resolveActiveRoutedQuestionIds } from './survey-routing-progress'
import type { SurveyDefinition, SurveyQuestion, SurveyQuestionType } from './survey-question.types'

/** Per-question average seconds — matches campaign-service `instrument-draft.logic`. */
const ECT_SECONDS_BY_TYPE: Record<SurveyQuestionType, number> = {
  multiple_choice: 20,
  yes_no: 10,
  checkbox: 25,
  dropdown: 20,
  picture_choice: 25,
  ranking: 35,
  short_text: 45,
  long_text: 90,
  date: 20,
  rating: 15,
  likert: 30,
  slider: 20,
  video: 60,
  unsupported: 0,
}

export function ectSecondsForQuestion(question: SurveyQuestion): number {
  return ECT_SECONDS_BY_TYPE[question.type] ?? 0
}

export function sumEctSecondsForQuestions(questions: readonly SurveyQuestion[]): number {
  let total = 0
  for (const q of questions) {
    total += ectSecondsForQuestion(q)
  }
  return total
}

/** Matches campaign-service `ectSecondsToDisplayMinutes`. */
export function ectSecondsToDisplayMinutes(seconds: number): number {
  return Math.ceil(seconds / 60)
}

/**
 * Remaining ECT from the current question through the end of the active routed path.
 * TC-SPW-42 / BFP-507 — recalculates when routing or skip logic changes the path.
 */
export function resolveRemainingEctMinutes(params: {
  definition: SurveyDefinition | null
  routingPath: string[]
  currentQuestionId: string | null
  hasRoutingRules: boolean
  routingRules?: QuestionRoutingRules[]
  answersByQuestionId?: AnswersByQuestionId
  fallbackMinutes?: number | null
  /** When true, only the current question remains on the routed path. */
  endsAtCurrentQuestion?: boolean
}): number | null {
  if (!params.definition || !params.currentQuestionId) {
    return params.fallbackMinutes ?? null
  }

  const activePathIds = resolveActiveRoutedQuestionIds({
    routingPath: params.routingPath,
    currentQuestionId: params.currentQuestionId,
    definition: params.definition,
    hasRoutingRules: params.hasRoutingRules,
    routingRules: params.routingRules,
    answersByQuestionId: params.answersByQuestionId,
    endsAtCurrentQuestion: params.endsAtCurrentQuestion,
  })

  const currentIdx = activePathIds.indexOf(params.currentQuestionId)
  let remainingIds: string[]
  if (params.endsAtCurrentQuestion || currentIdx < 0) {
    remainingIds = [params.currentQuestionId]
  } else {
    remainingIds = activePathIds.slice(currentIdx)
  }

  const questionById = new Map(params.definition.questions.map((q) => [q.id, q]))
  const remainingQuestions = remainingIds
    .map((id) => questionById.get(id))
    .filter((q): q is SurveyQuestion => q != null)

  if (remainingQuestions.length === 0) {
    return params.fallbackMinutes ?? null
  }

  const seconds = sumEctSecondsForQuestions(remainingQuestions)
  if (seconds <= 0) {
    return null
  }

  return Math.max(1, ectSecondsToDisplayMinutes(seconds))
}
